import {
  charitableOptions,
  familyOptions,
  organizationalOptions,
  professionalOptions,
} from 'common/edge-filter-cards/options';
import {
  RELATIONSHIP_TYPE_CHARITABLE,
  RELATIONSHIP_TYPE_EDUCATION,
  RELATIONSHIP_TYPE_ENGAGEMENT,
  RELATIONSHIP_TYPE_ATTENDED,
  RELATIONSHIP_TYPE_FAMILY,
  RELATIONSHIP_TYPE_ORGANIZATIONAL,
  RELATIONSHIP_TYPE_PERSONAL,
} from 'store/graph/constants';
import {
  baseEdgeColor,
  baseNodeColor,
  contextNodeColors,
  edgeColors,
  nodeColors,
  nodeTextColor,
  nodeType,
  affiliationComboColors,
  comboStyles,
} from './constants';
import { currencyFormatter } from './formatters/currency';
import {
  canAddAffiliation,
  canAddAge,
  canAddConstituency,
  canAddDeceased,
  canAddDegrees,
  canAddFundPurpose,
  canAddProspect,
  canAddRegion,
  canAddTotalConnections,
  canAddTotalLifetimeGiving,
} from './graph-validators';

const NODE_LABEL_TEXT_WRAP = {
  textWrap: 'normal',
  maxWidth: 300,
};

export const getItems = (
  queryResponse,
  selectedNodeId,
  isCustomView = false,
  isRegionSearch = false,
) => {
  // Defensive: handle cases where the backend returns null/invalid JSON
  if (!queryResponse) return;
  const { edges, vertices } = queryResponse;
  if (edges !== undefined && vertices !== undefined && edges !== null && vertices !== null) {
    const { nodes, nodeCount } = makeNodes(vertices, selectedNodeId);
    const links = makeLinks(edges, nodes);
    if (isCustomView && !isRegionSearch) {
      const updatedNodes = setupTraversalProperty(nodes, links, selectedNodeId);
      return { nodes: updatedNodes, links, nodeCount };
    }
    return { nodes, links, nodeCount };
  }
};

const makeNodes = (vertices, selectedNodeId) => {
  let nodes = {};
  let nodeCount = 0;
  for (const key in vertices) {
    const vertex = vertices[key];
    const label = vertex.label.toLowerCase();
    const node = getNode(label, vertex, key, selectedNodeId);
    // if item is not empty, set it
    nodes[key] = node;
    nodeCount++;
  }
  return { nodes, nodeCount };
};

const makeLinks = (edges, nodes) => {
  let links = {};
  for (const key in edges) {
    const edge = edges[key];
    const { inV, outV } = edge;
    // remove links with nodes that do not exist
    if (nodes[inV] && nodes[outV]) {
      // filter_match edges get special styling
      if (edge.label === 'filter_match') {
        links[`${edge.inV}/p/${edge.outV}`] = {
          id1: edge.inV,
          id2: edge.outV,
          width: 3,
          color: '#38b2d9',
          activeColor: '#38b2d9',
          data: {
            id: edge.id,
            inV: edge.inV,
            label: edge.label,
            outV: edge.outV,
            ...edge.properties,
            connection: 'filter_match',
            connectionType: 'Filter Match',
          },
        };
        continue;
      }
      const {
        connection,
        connectionText,
        topNodeRelationshipType,
        bottomNodeRelationshipType,
        activeColor,
      } = getConnectionType(edge.properties, nodes, inV, outV);
      links[`${edge.inV}/p/${edge.outV}`] = {
        id1: edge.inV,
        id2: edge.outV,
        width: 5,
        color: baseEdgeColor,
        activeColor,
        data: {
          id: edge.id,
          inV: edge.inV,
          label: edge.label,
          outV: edge.outV,
          ...edge.properties,
          connection,
          connectionType: connectionText,
          bottomNodeRelationshipType,
          topNodeRelationshipType,
        },
      };
    }
  }
  return links;
};

const getNode = (label, vertex, nodeKey, selectedContextId) => {
  let item = {};
  const isMostConnected = vertex.properties.mostConnected ?? false;
  let node_color = undefined;
  //TODO: CONTEXT IS BEING OVEDRWRITTEN IF IT ALREADY EXISTS IN RESPONSE
  if (label === 'person') {
    const {
      firstname,
      traversal,
      total_first_connections,
      MetroRegion,
      crmurl,
      rating,
      maritalstatus,
      age,
      gender,
      primaryaffiliation,
      affiliations,
      lifetimehouseholdgiving,
      maidenname,
      uiucltg,
      uisltg,
      uicltg,
      constituency,
      isdeceased,
      keyname,
      jobtitle,
    } = vertex.properties;

    node_color = nodeColors.person;
    const name = `${firstname} ${keyname}`;
    let selected = {};
    if (nodeKey === selectedContextId) {
      selected = {
        color: nodeColors.contextNode,
        border: { color: nodeColors.person, width: 5 },
        label: {
          bold: true,
          color: nodeTextColor,
          text: name,
          backgroundColor: 'rgba(0,0,0,0)',
          ...NODE_LABEL_TEXT_WRAP,
        },
        halos: [{ color: nodeColors.contextNode, radius: 33, width: 4 }],
        size: 8,
        isContextNode: true,
      };
    }
    const { mostConnected, highestCapacity, largestDonor } = getSizingTypes(
      total_first_connections,
      lifetimehouseholdgiving,
      rating,
    );
    item = {
      id: nodeKey,
      isContextNode: false,
      ...getNodeItem(name, mostConnected),
      ...selected,
      cutout: true,
      traversal,
      nodeType: nodeType.Person,
      data: {
        ...vertex.properties,
        name: name,
        age: age,
        constituency: constituency,
        gender: gender,
        maiden: maidenname,
        maritalStatus: maritalstatus,
        affliationUnit: primaryaffiliation,
        affiliations: affiliations,
        totalConnections: total_first_connections,
        prospectStatus: rating,
        lifetimeGiving: lifetimehouseholdgiving,
        region: MetroRegion,
        mostConnectedSize: mostConnected,
        highestCapacitySize: highestCapacity,
        largestDonorSize: largestDonor,
        tedUrl: crmurl,
        isdeceased: isdeceased,
        title: jobtitle,
        snapshot: {
          total: currencyFormatter(lifetimehouseholdgiving),
          universityGiving: [
            {
              name: 'UIUC',
              amount: currencyFormatter(uiucltg),
            },
            {
              name: 'UIC',
              amount: currencyFormatter(uicltg),
            },
            {
              name: 'UIS',
              amount: currencyFormatter(uisltg),
            },
          ],
        },
        nodeType: nodeType.Person,
        nodeKey,
        isContextNode: nodeKey === selectedContextId,
      },
    };
  } else if (label === 'org') {
    let selected = {};
    const {
      industry,
      traversal,
      total_first_connections,
      fullname,
      crmurl,
      website,
      uiucltg,
      uisltg,
      uicltg,
      constituency,
      MetroRegion,
      lifetimehouseholdgiving,
      primaryaffiliation,
    } = vertex.properties;
    node_color = nodeColors.organization;
    if (nodeKey === selectedContextId) {
      selected = {
        color: nodeColors.contextNode,
        border: { color: nodeColors.organization, width: 5 },
        label: {
          bold: true,
          color: nodeTextColor,
          text: fullname,
          backgroundColor: 'rgba(0,0,0,0)',
          ...NODE_LABEL_TEXT_WRAP,
        },
        halos: [{ color: nodeColors.contextNode, radius: 33, width: 4 }],
        size: 8,
        isContextNode: true,
      };
    }
    const { mostConnected, highestCapacity, largestDonor } = getSizingTypes(
      total_first_connections,
      lifetimehouseholdgiving,
      undefined,
    );
    item = {
      id: nodeKey,
      isContextNode: false,
      ...getNodeItem(fullname, mostConnected),
      ...selected,
      cutout: true,
      traversal,
      nodeType: nodeType.Organization,
      data: {
        ...vertex.properties,
        industry: industry,
        name: fullname,
        organizationType: '',
        totalConnections: total_first_connections,
        website: website,
        constituency: constituency,
        tedUrl: crmurl,
        region: MetroRegion,
        affliationUnit: primaryaffiliation,
        nodeKey,
        mostConnectedSize: mostConnected,
        highestCapacitySize: highestCapacity,
        largestDonorSize: largestDonor,
        snapshot: {
          total: currencyFormatter(lifetimehouseholdgiving),
          universityGiving: [
            {
              name: 'UIUC',
              amount: currencyFormatter(uiucltg),
            },
            {
              name: 'UIC',
              amount: currencyFormatter(uicltg),
            },
            {
              name: 'UIS',
              amount: currencyFormatter(uisltg),
            },
          ],
        },
        isContextNode: nodeKey === selectedContextId,
        nodeType: nodeType.Organization,
      },
    };
  } else if (label === 'fund') {
    const {
      total_first_connections,
      traversal,
      departmentdescription,
      crmurl,
      fundname,
      institutiondescription,
      purpose,
      collegedescription,
      MetroRegion,
      primaryaffiliation,
    } = vertex.properties;
    let selected = {};
    node_color = nodeColors.fund;
    if (nodeKey === selectedContextId) {
      selected = {
        color: nodeColors.contextNode,
        label: {
          bold: true,
          color: nodeTextColor,
          backgroundColor: 'rgba(0,0,0,0)',
          text: fundname,
          ...NODE_LABEL_TEXT_WRAP,
        },
        border: { color: nodeColors.fund, width: 5 },
        halos: [{ color: nodeColors.contextNode, radius: 33, width: 4 }],
        size: 8,
        isContextNode: true,
      };
    }
    const { mostConnected } = getSizingTypes(
      total_first_connections,
      undefined,
      undefined,
    );
    item = {
      id: nodeKey,
      isContextNode: false,
      ...getNodeItem(fundname, mostConnected),
      ...selected,
      cutout: true,
      nodeType: nodeType.Fund,
      traversal,
      data: {
        ...vertex.properties,
        department: departmentdescription,
        totalConnections: total_first_connections,
        fundName: fundname,
        name: fundname,
        nodeKey,
        institution: institutiondescription,
        region: MetroRegion,
        purpose: purpose,
        college: collegedescription,
        affliationUnit: primaryaffiliation,
        isContextNode: nodeKey === selectedContextId,
        nodeType: nodeType.Fund,
        mostConnectedSize: mostConnected,
        highestCapacitySize: 1,
        largestDonorSize: 1,
        tedUrl: crmurl,
      },
    };
  } else if (label === 'education') {
    const {
      title,
      traversal,
      total_first_connections,
      crmurl,
      attribute4,
      attribute3,
      attribute2,
      attribute1,
      MetroRegion,
      titlecode,
      affiliation,
    } = vertex.properties;
    node_color = nodeColors.education;
    const text = title || titlecode;
    let selected = {};
    if (nodeKey === selectedContextId) {
      selected = {
        color: nodeColors.contextNode,
        border: { color: nodeColors.education, width: 5 },
        label: {
          bold: true,
          color: nodeTextColor,
          backgroundColor: 'rgba(0,0,0,0)',
          text,
          ...NODE_LABEL_TEXT_WRAP,
        },
        halos: [{ color: nodeColors.contextNode, radius: 33, width: 4 }],
        size: 8,
        isContextNode: true,
      };
    }
    const { mostConnected } = getSizingTypes(
      total_first_connections,
      undefined,
      undefined,
    );
    item = {
      id: nodeKey,
      isContextNode: false,
      ...getNodeItem(text, mostConnected),
      ...selected,
      cutout: true,
      nodeType: nodeType.Education,
      traversal,
      data: {
        ...vertex.properties,
        totalConnections: total_first_connections,
        institution: attribute1,
        region: MetroRegion,
        college: attribute2,
        department: attribute3,
        major: attribute4,
        name: text,
        nodeKey,
        affliationUnit: affiliation,
        mostConnectedSize: mostConnected,
        highestCapacitySize: 1,
        largestDonorSize: 1,
        isContextNode: nodeKey === selectedContextId,
        nodeType: nodeType.Education,
        tedUrl: crmurl,
      },
    };
  } else if (label === 'engagement') {
    const {
      fullname,
      traversal,
      total_first_connections,
      crmurl,
      grouptype,
      description,
      MetroRegion,
      primaryaffiliation,
    } = vertex.properties;
    const title = fullname;
    let selected = {};
    node_color = nodeColors.engagement;
    if (nodeKey === selectedContextId) {
      selected = {
        color: nodeColors.contextNode,
        border: { color: nodeColors.engagement, width: 5 },
        label: {
          bold: true,
          color: nodeTextColor,
          backgroundColor: 'rgba(0,0,0,0)',
          text: title,
          ...NODE_LABEL_TEXT_WRAP,
        },
        halos: [{ color: nodeColors.contextNode, radius: 33, width: 4 }],
        size: 8,
        isContextNode: true,
      };
    }
    const { mostConnected } = getSizingTypes(
      total_first_connections,
      undefined,
      undefined,
    );
    item = {
      id: nodeKey,
      isContextNode: false,
      ...getNodeItem(title, mostConnected),
      ...selected,
      cutout: true,
      traversal,
      nodeType: nodeType.Engagement,
      data: {
        ...vertex.properties,
        totalConnections: total_first_connections,
        groupType: grouptype,
        region: MetroRegion,
        committeeType: description,
        name: title,
        nodeKey,
        affliationUnit: primaryaffiliation,
        mostConnectedSize: mostConnected,
        highestCapacitySize: 1,
        largestDonorSize: 1,
        isContextNode: nodeKey === selectedContextId,
        nodeType: nodeType.Engagement,
        tedUrl: crmurl,
      },
    };
  } else if (label === 'event') {
    const {
      fullname,
      traversal,
      total_first_connections,
      crmurl,
      type,
      status,
      eventlocationname,
      eventlocationcity,
      eventlocationstate,
      MetroRegion,
      affiliation,
      eventstartdate,
      eventenddate,
      description,
      lookupid,
    } = vertex.properties;

    node_color = nodeColors.event;
    const title = fullname?.[0]?.value || fullname || 'Unknown Event';
    
    let selected = {};
    if (nodeKey === selectedContextId) {
      selected = {
        color: nodeColors.contextNode,
        border: { color: nodeColors.event, width: 5 },
        label: {
          bold: true,
          color: nodeTextColor,
          backgroundColor: 'rgba(0,0,0,0)',
          text: title,
        },
        halos: [{ color: nodeColors.contextNode, radius: 33, width: 4 }],
        size: 8,
        isContextNode: true,
      };
    }

    const formatEventLocation = (city, state) => {
      const cityVal = city?.[0]?.value || city || '';
      const stateVal = state?.[0]?.value || state || '';
      if (cityVal && stateVal) return `${cityVal}, ${stateVal}`;
      return cityVal || stateVal || '';
    };

    const { mostConnected } = getSizingTypes(
      total_first_connections,
      undefined,
      undefined,
    );

    item = {
      id: nodeKey,
      isContextNode: false,
      ...getNodeItem(title, 1),
      ...selected,
      cutout: true,
      traversal: traversal?.[0]?.value || traversal,
      nodeType: nodeType.Event,
      data: {
        ...vertex.properties,
        name: title,
        lookupId: lookupid?.[0]?.value || lookupid,
        eventType: type?.[0]?.value || type,
        status: status?.[0]?.value || status,
        venue: eventlocationname?.[0]?.value || eventlocationname,
        city: eventlocationcity?.[0]?.value || eventlocationcity,
        state: eventlocationstate?.[0]?.value || eventlocationstate,
        location: formatEventLocation(eventlocationcity, eventlocationstate),
        region: MetroRegion?.[0]?.value || MetroRegion,
        affiliationUnit: affiliation?.[0]?.value || affiliation,
        startDate: eventstartdate?.[0]?.value || eventstartdate,
        endDate: eventenddate?.[0]?.value || eventenddate,
        description: description?.[0]?.value || description,
        nodeKey,
        tedUrl: crmurl?.[0]?.value || crmurl,
        isContextNode: nodeKey === selectedContextId,
        nodeType: nodeType.Event,
      },
    };
  } else if (label === 'filter_session') {
    const name = vertex.properties?.name || 'Filter Session';
    const filterCount = vertex.properties?.filterCount;
    const resultCount = vertex.properties?.resultCount;
    const filters = vertex.properties?.filters;
    const createdAt = vertex.properties?.createdAt;
    item = {
      id: nodeKey,
      isContextNode: true,
      color: '#38b2d9',
      border: { color: '#38b2d9', width: 5 },
      label: {
        bold: true,
        color: nodeTextColor,
        text: '\uD83D\uDD0D Filter Session',
        backgroundColor: 'rgba(0,0,0,0)',
      },
      halos: [{ color: '#38b2d9', radius: 33, width: 4 }],
      size: 8,
      cutout: true,
      nodeType: 'filter_session',
      data: {
        name,
        filterCount,
        resultCount,
        filters,
        createdAt,
        nodeType: 'filter_session',
        nodeKey,
        isContextNode: true,
      },
    };
  }

  if (isMostConnected) {
    item = {
      ...item,
      color: contextNodeColors.RoyalPurple.lighter,
      border: { color: node_color, width: 5 },
      label: {
        bold: true,
        color: nodeTextColor,
        text: item.label.text,
        backgroundColor: 'rgba(0,0,0,0)',
        ...NODE_LABEL_TEXT_WRAP,
      },
      halos: [
        { color: contextNodeColors.RoyalPurple.hex, radius: 33, width: 4 },
      ],
    };
  }

  return item;
};

// top relaation ties to outnode, bottom relationship ties inNode
const getConnectionType = (properties, nodes, inNodeId, outNodeId) => {
  const relationshipcombo =
    properties.relationshipcombo || properties.RelationshipCombo || '';
  // is a non normal edge split
  const isNotNormalEdge = isNonNormalEdge(relationshipcombo);
  // relationship combo gets split
  const [topRelationshipType, bottomRelationshipType] = !isNotNormalEdge
    ? relationshipcombo.split(/[-]+/)
    : [relationshipcombo, relationshipcombo];
  const inNode = nodes[inNodeId]?.data;
  const outNode = nodes[outNodeId]?.data;

  let connectionText = '';
  let activeColor = '';
  // TODO: check the dictionary types and set. and make colors a constant that be changed from one place
  if (!inNode || !outNode)
    return { connectionText, topNode: {}, bottomNode: {} };
  if (familyOptions[relationshipcombo] !== undefined) {
    connectionText = RELATIONSHIP_TYPE_FAMILY;
    activeColor = edgeColors.family;
  } else if (professionalOptions[relationshipcombo] !== undefined) {
    connectionText = RELATIONSHIP_TYPE_PERSONAL;
    activeColor = edgeColors.personal;
  } else if (organizationalOptions[relationshipcombo] !== undefined) {
    connectionText = RELATIONSHIP_TYPE_ORGANIZATIONAL;
    activeColor = edgeColors.professional;
  } else if (
    inNode.nodeType === nodeType.Fund ||
    outNode.nodeType === nodeType.Fund ||
    charitableOptions[relationshipcombo] !== undefined
  ) {
    connectionText = RELATIONSHIP_TYPE_CHARITABLE;
    activeColor = edgeColors.charitable;
  } else if (
    inNode.nodeType === nodeType.Education ||
    outNode.nodeType === nodeType.Education
  ) {
    connectionText = RELATIONSHIP_TYPE_EDUCATION;
    activeColor = edgeColors.education;
  } else if (
    inNode.nodeType === nodeType.Engagement ||
    outNode.nodeType === nodeType.Engagement
  ) {
    connectionText = RELATIONSHIP_TYPE_ENGAGEMENT;
    activeColor = edgeColors.engagement;
  } else if (
    inNode.nodeType === nodeType.Event ||
    outNode.nodeType === nodeType.Event
  ) {
    connectionText = RELATIONSHIP_TYPE_ATTENDED;
    activeColor = edgeColors.attended;
  }

  return {
    connectionText,
    connection: relationshipcombo,
    topNodeRelationshipType: topRelationshipType,
    bottomNodeRelationshipType: bottomRelationshipType,
    activeColor,
  };
};

export const getSizingTypes = (
  totalConnections,
  lifetimeGiving,
  prospectStatus,
) => {
  let mostConnected = getSizeMostConnected(totalConnections);
  let highestCapacity = getSizeProspectStatus(prospectStatus);
  let largestDonor = getSizeLargestDonor(lifetimeGiving);
  return { mostConnected, highestCapacity, largestDonor };
};

export const getSizeMostConnected = (totalConnections) => {
  if (!totalConnections || totalConnections <= 10) {
    return 1;
  }
  if (totalConnections <= 50) {
    return 2;
  }
  if (totalConnections <= 100) {
    return 3;
  }
  if (totalConnections <= 500) {
    return 4;
  }
  if (totalConnections <= 1000) {
    return 6;
  }
  if (totalConnections > 1000) {
    return 8;
  }
};

export const sizeProspectMap = {
  '< $2,500': 1,
  '$2,500 - $4,999': 1,
  '$5,000 - $9,999': 1,
  '$10,000 - $24,999': 2,
  '$25,000 - $49,999': 2,
  '$50,000 - $99,999': 3,
  '$100,000 - $249,999': 3,
  '$250,000 - $499,999': 4,
  '$500,000 - $999,999': 4,
  '$1,000,000 - $4,999,999': 5,
  '$5,000,000 -$9,999,999': 5,
  '$10,000,000 - $24,999,999': 6,
  '$25,000,000 - $49,999,999': 6,
  '$50,000,000 - $99,999,999': 7,
  '$100,000,000 - $249,999,999': 7,
  '$250,000,000+': 8,
};

export const getSizeProspectStatus = (prospectStatus) => {
  if (!prospectStatus) {
    return 1;
  }
  return sizeProspectMap[prospectStatus] || 1;
};

export const getSizeLargestDonor = (lifetimeGiving) => {
  if (!lifetimeGiving || lifetimeGiving < 10000) {
    return 1;
  }
  if (lifetimeGiving < 50000) {
    return 2;
  }
  if (lifetimeGiving < 250000) {
    return 3;
  }
  if (lifetimeGiving < 1000000) {
    return 4;
  }
  if (lifetimeGiving < 50000000) {
    return 5;
  }
  if (lifetimeGiving < 250000000) {
    return 6;
  }
  if (lifetimeGiving >= 250000000) {
    return 8;
  }
};

export const getSize = (nodeSizingType, properties) => {
  if (!properties) return 3; // Default size if properties are missing
  let size = properties.mostConnectedSize || 3;
  if (nodeSizingType === 1) {
    size = properties.highestCapacitySize || 3;
  }
  if (nodeSizingType === 2) {
    size = properties.largestDonorSize || 3;
  }
  return size;
};

export const getNodeItem = (text, size) => {
  return {
    color: baseNodeColor,
    border: { color: baseNodeColor },
    label: {
      text,
      color: nodeTextColor,
      fontSize: 9,
      center: true,
      backgroundColor: 'rgba(0,0,0,0)',
      ...NODE_LABEL_TEXT_WRAP,
    },
    size,
  };
};

/**
 * Get node styling with optional multi-affiliation gold border
 * Shared logic for selection and hover states
 */
const getNodeInteractionStyles = (node, selectedAffiliations = null) => {
  const { color, text } = getNodeColorAndText(node);
  const isMultiAffiliation = selectedAffiliations && hasMultipleAffiliations(node, selectedAffiliations);
  
  const borderColor = isMultiAffiliation ? comboStyles.multiAffiliationBorder : '#fff';
  const borderWidth = isMultiAffiliation ? 3 : 2;
  const haloColor = isMultiAffiliation ? comboStyles.multiAffiliationBorder : color;
  
  return {
    color,
    border: { color: borderColor, width: borderWidth },
    label: {
      bold: true,
      color: nodeTextColor,
      backgroundColor: 'rgba(0,0,0,0)',
      text,
      ...NODE_LABEL_TEXT_WRAP,
    },
    halos: [{ color: haloColor, radius: 30, width: borderWidth }],
  };
};

export const GetNodeSelectionStyles = (node, selectedAffiliations = null) => 
  getNodeInteractionStyles(node, selectedAffiliations);

export const GetNodeHoverStyles = (node, selectedAffiliations = null) => 
  getNodeInteractionStyles(node, selectedAffiliations);

export const GetBaseNodeStyles = (node, nodeSizingType) => {
  const size = getSize(nodeSizingType, node.data);
  const text = node.data.name;
  return {
    color: baseNodeColor,
    border: { color: baseNodeColor },
    label: {
      text: text,
      color: nodeTextColor,
      fontSize: 9,
      center: true,
      backgroundColor: 'rgba(0,0,0,0)',
      ...NODE_LABEL_TEXT_WRAP,
    },
    halos: [],
    size,
  };
};

export const getNodeColor = (type) => {
  switch (type) {
    case nodeType.Person:
      return nodeColors.person;
    case nodeType.Education:
      return nodeColors.education;
    case nodeType.Fund:
      return nodeColors.fund;
    case nodeType.Engagement:
      return nodeColors.engagement;
    case nodeType.Organization:
      return nodeColors.organization;
    case nodeType.Event:
      return nodeColors.event;
    default:
      return nodeColors.person;
  }
};

export const getNodeColorAndText = (node) => {
  if (!node) {
    return { color: baseNodeColor, text: '' };
  }
  const color = getNodeColor(node.nodeType);
  return { color, text: node?.data?.name || '' };
};

export const isLink = (item) => {
  return item.id1 !== undefined;
};

const NonNormalEdges = {
  'in-law': 'in-law',
};

const isNonNormalEdge = (edge) => {
  return NonNormalEdges[edge.toLowerCase()] !== undefined;
};

export const IsObjectEmpty = (obj) => {
  for (const i in obj) {
    return false;
  }
  return true;
};

/**
 * Check if a node matches 2+ selected affiliations
 */
export const hasMultipleAffiliations = (node, selectedAffiliations) => {
  if (!node?.data || !selectedAffiliations) return false;
  
  const affiliationsString = (node.data.affiliations || node.data.affliationUnit || '').toLowerCase();
  if (!affiliationsString) return false;
  
  const selectedKeys = Object.keys(selectedAffiliations).map(k => k.toLowerCase());
  const matchCount = selectedKeys.filter(key => affiliationsString.includes(key)).length;
  
  return matchCount >= 2;
};

/**
 * Callback for ReGraph onCombineNodes to style affiliation combos
 * @param {Object} params - ReGraph callback params
 * @param {Function} params.setStyle - Function to set combo style
 * @param {Object} params.combo - Combo data
 * @param {Object} params.nodes - Object with node IDs as keys and node data as values
 * @param {string} params.id - Combo ID
 * @param {boolean} isOpen - Whether the combo should be open (expanded)
 * @param {Object} contextIds - Object with context node IDs as keys
 */
export const styleAffiliationCombo = (params, isOpen = false, contextIds = {}) => {
  const { setStyle, combo, nodes, id } = params;
  const affiliationName = combo?.affliationUnit || 'Unknown Affiliation';
  const colors = affiliationComboColors[affiliationName.toLowerCase()] || affiliationComboColors.default;
  const nodeCount = Object.keys(nodes || {}).length;

  // Do not auto-expand combos for single-node clusters; keep closed style
  // so the UI remains consistent and clickable in tests and real use.

  // Check if this combo contains the context/target node
  const containsContextNode = Object.keys(nodes || {})
    .some(nodeId => contextIds[nodeId] !== undefined);

  // Glyph indicator for combos containing the context node
  const contextNodeGlyph = containsContextNode ? [{
    position: 'ne',
    size: 1.5,
    color: nodeColors.contextNode,
    border: { color: '#fff', width: 1 },
  }] : undefined;

  // Dynamic sizing based on node count - more dramatic differences
  const calculateSize = (count) => {
    if (count <= 5) return 5;    // Small cluster
    if (count <= 15) return 10;   // Medium cluster
    if (count <= 30) return 15;   // Large cluster
    return 20;                    // Extra large
  };

  const labelText = `${affiliationName} (${nodeCount})`;
  const comboSize = calculateSize(nodeCount);

  // ReGraph expects both open and closed styles defined together
  setStyle({
    open: isOpen,
    size: comboSize +1,
    // Open combo styling (when expanded) - large fontSize for visibility
    label: {
      text: labelText,
      fontSize: 16,
      bold: true,
      color: '#000000',
      backgroundColor: 'rgba(255, 0, 0, 0)',
    },
    color: colors.bg,
    border: {
      width: comboStyles.borderWidth,
      color: colors.border,
      lineStyle: comboStyles.borderStyle,
    },
    glyphs: contextNodeGlyph,
    // Closed combo styling (when collapsed)
    closedStyle: {
      size: comboSize-1,
      label: {
        text: labelText,
        fontSize: 12,
        bold: true,
        color: '#000000',
        backgroundColor: 'rgba(255, 0, 0, 0)',
        position: 's',
      },
      color: colors.bg,
      border: {
        width: comboStyles.borderWidth,
        color: colors.border,
        lineStyle: comboStyles.borderStyle,
      },
      glyphs: contextNodeGlyph,
    },
  });
};

export const canAddNode = (
  node,
  isRegionsFilterEmpty,
  isConstituencyEmpty,
  constitencies,
  nodeFilters,
  hiddenNodes = {},
  affiliationClusterMode = false,
) => {
  if (hiddenNodes[node?.id]) {
    return false;
  }
  const {
    region,
    affiliation,
    lifetimeGiving,
    prospectStatus,
    totalConnections,
    degreeOfSeparation,
    showDeceasedConnections,
    ageFilter,
    fundPurpose,
  } = nodeFilters;
  return (
    canAddRegion(node, region, isRegionsFilterEmpty) &&
    canAddDegrees(node, degreeOfSeparation) &&
    canAddProspect(node, prospectStatus) &&
    canAddTotalConnections(node, totalConnections) &&
    canAddTotalLifetimeGiving(node, lifetimeGiving) &&
    // Bypass affiliation filter when clustering mode is ON (show all nodes)
    (affiliationClusterMode || canAddAffiliation(node, affiliation)) &&
    canAddConstituency(node, constitencies, isConstituencyEmpty) &&
    canAddDeceased(node, showDeceasedConnections) &&
    canAddAge(node, ageFilter) &&
    canAddFundPurpose(node, fundPurpose)
  );
};

export const setupTraversalProperty = (nodes, links, selectedNodeId) => {
  const items = {};
  
  // Build adjacency map once: O(n) where n = number of links
  const adjacencyMap = new Map();
  for (const linkId in links) {
    const link = links[linkId];
    // Links have id1 and id2 properties, or we can parse from linkId format "id1/p/id2"
    let id1, id2;
    if (link && link.id1 && link.id2) {
      id1 = link.id1;
      id2 = link.id2;
    } else if (linkId.includes('/p/')) {
      [id1, id2] = linkId.split('/p/');
    } else {
      continue; // Skip invalid links
    }
    
    if (!adjacencyMap.has(id1)) {
      adjacencyMap.set(id1, new Set());
    }
    if (!adjacencyMap.has(id2)) {
      adjacencyMap.set(id2, new Set());
    }
    adjacencyMap.get(id1).add(id2);
    adjacencyMap.get(id2).add(id1);
  }
  
  // BFS traversal: O(n + m) where n = nodes, m = edges
  const visited = new Set();
  const queue = [{ nodeId: selectedNodeId, level: 0 }];
  visited.add(selectedNodeId);
  
  // Add selected node at level 0 - preserve original node structure
  const selectedNode = nodes[selectedNodeId];
  if (selectedNode && (selectedNode.node || selectedNode.data)) {
    items[selectedNodeId] = { ...selectedNode, traversal: 0 };
  }
  
  while (queue.length > 0) {
    const { nodeId, level } = queue.shift();
    const node = nodes[nodeId];
    // Skip if node doesn't exist or doesn't have proper structure
    if (!node || (!node.node && !node.data)) continue;
    
    // Set traversal level if not already set (preserve lower levels)
    if (items[nodeId] === undefined && level > 0) {
      items[nodeId] = { ...node, traversal: level };
    }
    
    // Traverse to next level (max level 3)
    if (level < 3) {
      const neighbors = adjacencyMap.get(nodeId) || new Set();
      for (const neighborId of neighbors) {
        const neighborNode = nodes[neighborId];
        if (!visited.has(neighborId) && neighborNode && (neighborNode.node || neighborNode.data)) {
          visited.add(neighborId);
          queue.push({ nodeId: neighborId, level: level + 1 });
        }
      }
    }
  }
  
  // Add unconnected nodes (not reachable from selected node) - preserve original structure
  for (const nodeId in nodes) {
    if (!items[nodeId] && nodes[nodeId]) {
      // Ensure we preserve the full node structure
      const node = nodes[nodeId];
      if (node && (node.node || node.data)) {
        items[nodeId] = { ...node, traversal: 1 };
      }
    }
  }
  
  return items;
};

// Keep getConnectedNodes for backward compatibility if used elsewhere
const getConnectedNodes = (nodeId, items, nodes, links) => {
  let nodesOnNextLevel = [];
  for (const linkId in links) {
    if (linkId.includes(nodeId)) {
      const [nodeOneId, nodeTwoId] = linkId.split('/p/');
      if (nodeOneId === nodeId) {
        // add nodeTwo
        if (items[nodeTwoId] === undefined) {
          nodesOnNextLevel.push(nodes[nodeTwoId]);
        }
      } else {
        if (items[nodeOneId] === undefined) {
          nodesOnNextLevel.push(nodes[nodeOneId]);
        }
      }
      delete links[linkId];
    }
  }
  return nodesOnNextLevel;
};
